import {useEffect} from 'react';

type UseStripListsParams = {
  enabled: boolean;
  isFocused: boolean;
  editorRef: React.RefObject<any>;
  onChangeValue: (markdown: string) => void;
};

/**
 * Strips lists from text when pasted into a text editor (`editorRef`)
 */
export function useStripLists({
  enabled,
  isFocused,
  editorRef,
  onChangeValue,
}: UseStripListsParams) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // eslint-disable-next-line complexity
    const handlePaste = (event: ClipboardEvent) => {
      if (!isFocused || !editorRef.current) {
        return;
      }

      const clipboardData =
        event.clipboardData || (window as any).clipboardData;
      const pastedText = clipboardData.getData('text/plain');
      const pastedHtml = clipboardData.getData('text/html');

      // Check for HTML lists or text bullet patterns
      const hasHtmlLists = /<ul|<ol|<li/i.test(pastedHtml);
      const hasTextBullets = /^\s*[-*+•·‣⁃]\s+|^\s*\d+\.\s+/m.test(pastedText);

      if (hasHtmlLists || hasTextBullets) {
        event.preventDefault();
        event.stopPropagation();

        let sanitizedText = pastedText;

        // Extract text from HTML list items if present
        if (hasHtmlLists && pastedHtml) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = pastedHtml;
          const listItems = tempDiv.querySelectorAll('li');
          if (listItems.length > 0) {
            sanitizedText = Array.from(listItems)
              .map((li) => li.textContent?.trim())
              .join('\n');
          }
        }

        // Strip any remaining bullet patterns
        sanitizedText = sanitizedText.replace(
          /^\s*[-*+•·‣⁃]\s+|^\s*\d+\.\s+/gm,
          ''
        );

        // Insert the sanitized text
        const currentMarkdown = editorRef.current.getMarkdown();
        const newMarkdown = currentMarkdown + sanitizedText;
        editorRef.current.setMarkdown(newMarkdown);
        onChangeValue(newMarkdown);
      }
    };

    document.addEventListener('paste', handlePaste, true);
    // eslint-disable-next-line consistent-return
    return () => document.removeEventListener('paste', handlePaste, true);
  }, [enabled, isFocused, editorRef, onChangeValue]);
}
